import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Button
} from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { useSnackBar } from '../../components/FlowBuilder/Common/SnackBar/index.tsx';
import Pagination from '../../components/FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import LongStringView from '../Shared/LongStringView/index.tsx';
import { SmsActions } from '../../Services/_actions/SMS/sms.actions.ts';
import DateRangeFilter from '../Shared/DateRangeFilter/DateRangeFilter.tsx';
import SearchBox from '../../components/FlowBuilder/Common/SearchBox/index.tsx';
import useDebounce from '../Shared/Delay/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import LoadingDisableWrapper from '../Shared/LoadingDisableWrapper/index.tsx';

interface SmsLog {
  _id: string;
  transaction_id: string;
  sms_cost: string | number;
  status: string;
  created_at: string;
  request_id: string | null;
  organisation_id: string;
  errors: string | null;
  hash_key: string | null;
  dlt_templateid?: string;
  phone: number | string;
  text: string;
}

/* ───────────────── Helpers ───────────────── */

const toYMD = (date: Date | null) =>
  date ? date.toISOString().slice(0, 10) : '';

const statusColor = (
  status: string
): 'default' | 'warning' | 'success' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

/* ───────────────── Component ───────────────── */

const SmsLog: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const actions = SmsActions();
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [phoneSearch, setPhoneSearch] = useState('');

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    phone: '',
    page: 0,
    page_size: 20,
  });

  const debouncedPhone = useDebounce(phoneSearch, 500);
  const fetchIdRef = useRef(0);

  /* ───────────── Fetch Logs (Single Source of Truth) ───────────── */

  const fetchLogs = async (f: typeof filters) => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);

    try {
      const res = await actions.getSmsLogs({
        ...f,
        page: f.page + 1, // backend is 1-based
      });
      if (fetchId !== fetchIdRef.current) return;
      if (!res?.status) {
        showSnackBar('Failed to fetch SMS logs', 'error');
        return;
      }

      setLogs(res?.data?.results ?? []);
      setTotalRecords(res?.data?.total ?? 0);
    } catch {
      showSnackBar('Error while fetching SMS logs', 'error');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  /* ───────────── Effects ───────────── */

  useEffect(() => {
    fetchLogs(filters);
  }, [filters]);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      phone: debouncedPhone,
      page: 0,
    }));
  }, [debouncedPhone]);

  /* ───────────── Handlers ───────────── */

  const handleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    setFilters(prev => ({
      ...prev,
      start_date: toYMD(startDate),
      end_date: toYMD(endDate),
      page: 0,
    }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({
      ...prev,
      page_size: parseInt(e.target.value, 10) || 20,
      page: 0,
    }));
  };

  // const handleDownloadReport = async () => {
  //   try {
  //     if (!filters.start_date || !filters.end_date) {
  //       showSnackBar('Please select start and end date', 'warning');
  //       return;
  //     }

  //     const res = await actions.downloadSmsReport({
  //       start_date: filters.start_date,
  //       end_date: filters.end_date,
  //     });

  //     const blob = new Blob([res.data], { type: 'text/csv' });
  //     const url = window.URL.createObjectURL(blob);

  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.download = `sms-report-${filters.start_date}-to-${filters.end_date}.csv`;
  //     document.body.appendChild(link);
  //     link.click();

  //     link.remove();
  //     window.URL.revokeObjectURL(url);
  //   } catch (error) {
  //     showSnackBar('Failed to download SMS report', 'error');
  //   }
  // };


  // const handleDownloadReport = async () => {
  //   try {
  //     if (!filters.start_date || !filters.end_date) {
  //       showSnackBar('Please select start and end date', 'warning');
  //       return;
  //     }

  //     const res = await actions.downloadSmsReport({
  //       ...filters,
  //     });

  //     const data = res?.data?.results || [];

  //     const worksheet = XLSX.utils.json_to_sheet(data);
  //     const workbook = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(workbook, worksheet, 'SMS Logs');

  //     XLSX.writeFile(
  //       workbook,
  //       `sms-report-${filters.start_date}-to-${filters.end_date}.xlsx`
  //     );
  //   } catch (error) {
  //     showSnackBar('Failed to download SMS report', 'error');
  //   }
  // };


  const handleDownloadReport = async () => {
    try {
      if (!filters.start_date || !filters.end_date) {
        showSnackBar('Please select start and end date', 'warning');
        return;
      }

      await actions.downloadSmsReport({
        ...filters,
      });
    } catch (error) {
      showSnackBar('Failed to download SMS report', 'error');
    }
  };

  /* ───────────── Columns (API keys = accessorKey) ───────────── */

  const columns = useMemo(
    () => [
      {
        accessorKey: 'phone',
        header: 'phone',
        size: 120,
      },
      {
        accessorKey: 'status',
        header: 'status',
        size: 100,
        Cell: ({ cell }: any) => (
          <Chip
            label={cell.getValue()}
            color={statusColor(cell.getValue())}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        ),
      },
      {
        accessorKey: 'transaction_id',
        header: 'Transaction ID',
        size: 200,
        Cell: ({ cell }: any) => (
          <LongStringView longString={cell.getValue() || ''} />
        ),
      },
      {
        accessorKey: 'dlt_templateid',
        header: 'DLT Template ID',
        size: 200,
        Cell: ({ cell }: any) => (
          <LongStringView longString={cell.getValue() || ''} />
        ),
      },
      {
        accessorKey: 'text',
        header: 'message',
        size: 320,
        enableSorting: false,
        Cell: ({ cell }: any) => (
          <LongStringView longString={cell.getValue() ?? ''} />
        ),
      },
      {
        accessorKey: 'errors',
        header: 'Error Message',
        size: 300,
        enableSorting: false,
        Cell: ({ cell }: any) =>
          cell.getValue() ? (
            <LongStringView longString={cell.getValue()} />
          ) : (
            <Chip
              label="No Error"
              color="success"
              size="small"
              variant="outlined"
            />
          ),
      },
      {
        accessorKey: 'sms_cost',
        header: 'cost',
        size: 80,
      },
      {
        accessorKey: 'created_at',
        header: 'created at',
        size: 170,
        Cell: ({ cell }: any) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleString()
            : '',
      },
    ],
    []
  );

  /* ───────────── Breadcrumbs ───────────── */

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'SMS', href: '/r/sms/list' },
    { label: 'SMS Logs' },
  ];

  /* ───────────── Render ───────────── */

  return (
    <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(_, item: { href?: string }) =>
          item?.href && navigate(item.href)
        }
      />

      <Typography variant="h4" fontWeight="bold" mb={2}>
        SMS Logs
      </Typography>

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={3}>
          <LoadingDisableWrapper loading = {isLoading}>
          <SearchBox
            value={phoneSearch}
            handleSearch={setPhoneSearch}
            placeholder="Search by phone number..."
            disabled={isLoading}
          />
          </LoadingDisableWrapper>
        </Grid>

        <Grid item xs />

        <Grid item>
          <DateRangeFilter
            onApply={handleDateChange}
            startDate={filters.start_date ? new Date(filters.start_date) : null}
            endDate={filters.end_date ? new Date(filters.end_date) : null}
          />
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            onClick={handleDownloadReport}
            disabled={!filters.start_date || !filters.end_date}
            size="small"
          >
            Download Report
          </Button>
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={logs}
        isLoading={isLoading}
        enableTopToolbar={false}
        enablePagination={false}
        renderBottomToolbar={() => (
          <Box display="flex" justifyContent="flex-end">
            <Pagination
              totalCount={totalRecords}
              rowsPerPage={filters.page_size}
              page={filters.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
    </Box>
  );
};

export default SmsLog;